import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  MapPin,
  Clock,
  Phone,
  CheckCircle,
  AlertCircle,
  Truck,
  CreditCard,
  Droplets,
} from "lucide-react";
import { Button } from "@radix-ui/themes";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Navbar from "../navigation/Navbar";
import MapComponent from "../pages/coverageComponents/MapComponent";
import { formatPrice } from "../../scripts/generalScript";
import Footer from "../navigation/Footer";

const CoverageFullPage = () => {
  const navigate = useNavigate();
  const [selectedCity, setSelectedCity] = useState("Barranquilla");

  const cities = [
    { name: "Barranquilla", comingSoon: false, active: true },
    { name: "Cartagena", comingSoon: true, active: false },
    { name: "Santa Marta", comingSoon: true, active: false },
  ];

  const deliveryFee = process.env.REACT_APP_DELIVERY_FEE || "3500";

  const notify = (cityName) =>
    toast(`¡Muy pronto llegaremos a ${cityName}!`, {
      icon: "🚀",
      duration: 3000,
      style: {
        borderRadius: "12px",
        background: "#222",
        color: "#fff",
        fontWeight: "500",
      },
    });

  const handleCityClick = (city) => {
    if (city.comingSoon) {
      notify(city.name);
    } else {
      setSelectedCity(city.name);
    }
  };

  const coverageFeatures = [
    {
      icon: <Truck className="w-6 h-6" />,
      title: "Entrega rápida",
      description: "Entregas en 24-48 horas en zona principal",
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: "Zona principal",
      description: "Envío completamente gratuito",
    },
    {
      icon: <CreditCard className="w-6 h-6" />,
      title: "Zona extendida",
      description: `Solo $${formatPrice(deliveryFee)} por botellón`,
    },
    {
      icon: <Droplets className="w-6 h-6" />,
      title: "Agua premium",
      description: "Purificada con los más altos estándares",
    },
  ];

  const operatingHours = [
    { day: "Lunes - Viernes", hours: "8:00 AM - 6:00 PM" },
    { day: "Sábados", hours: "8:00 AM - 4:00 PM" },
    { day: "Domingos", hours: "Cerrado" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <Navbar type="general" staticPosition={true} />

      {/* Hero Section */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="pt-24 pb-16 px-4"
      >
        <div className="max-w-6xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 fraunces"
          >
            Nuestra zona de <span className="text-blue-600">cobertura</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto"
          >
            Llevamos agua purificada de la más alta calidad directamente a donde
            la necesitas.
          </motion.p>
        </div>
        <div className="max-w-6xl mx-auto">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-wrap justify-center gap-4 my-8">
              {cities.map((city, index) => (
                <motion.div
                  key={city.name}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1, duration: 0.3 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    size="3"
                    variant={
                      selectedCity === city.name && city.active
                        ? "solid"
                        : "outline"
                    }
                    className={`px-6 py-3 transition-all ${
                      city.active
                        ? selectedCity === city.name
                          ? "bg-blue-600 text-white border-blue-600"
                          : "hover:bg-blue-50 border-blue-200"
                        : "opacity-75 hover:opacity-100"
                    }`}
                    onClick={() => handleCityClick(city)}
                  >
                    <div className="flex items-center gap-2">
                      <span>{city.name}</span>
                    </div>
                  </Button>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-3xl overflow-hidden border border-gray-200">
            <div className="aspect-[16/10] md:aspect-[21/9] w-full">
              <MapComponent />
            </div>
          </div>
        </div>
      </motion.section>

      {/* Coverage Details */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="py-16 px-4 bg-gray-50"
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 fraunces">
                Horarios de entrega
              </h3>
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                {operatingHours.map((schedule, index) => (
                  <div
                    key={index}
                    className={`flex justify-between items-center p-4 ${
                      index !== operatingHours.length - 1
                        ? "border-b border-gray-100"
                        : ""
                    }`}
                  >
                    <span className="font-medium text-gray-900">
                      {schedule.day}
                    </span>
                    <span className="text-gray-600 flex items-center gap-2">
                      <Clock size={16} />
                      {schedule.hours}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 fraunces">
                ¿Dudas sobre cobertura?
              </h3>
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <Phone className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semimedium text-gray-900">WhatsApp</h4>
                    <p className="text-gray-600 text-base">
                      Escríbenos para consultar tu zona
                    </p>
                  </div>
                </div>
                <Button
                  size="3"
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                  onClick={() =>
                    window.open("https://wa.me/573001234567", "_blank")
                  }
                >
                  Contactar por WhatsApp
                </Button>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="py-16 px-4 bg-blue-600"
      >
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 fraunces">
            ¿Listo para hacer tu pedido?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Agua purificada de calidad premium, entregada directamente en tu
            puerta.
          </p>
          <Button
            size="4"
            className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 text-lg font-semimedium"
            onClick={() => navigate("/nuevo-pedido")}
          >
            Hacer mi pedido ahora
          </Button>
        </div>
      </motion.section>
      <div className="bg-blue-600">
        <Footer />
      </div>
      <Toaster />
    </div>
  );
};

export default CoverageFullPage;
